import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
  Box,
  Container,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import img_1 from '../images/img_1.jpeg';
import img_11 from '../images/img_11.jpeg';
import img_12 from '../images/img_12.jpeg';
import img_13 from '../images/img_13.jpeg';
import img_14 from '../images/img_14.jpeg';
import img_15 from '../images/img_15.jpeg';
import img_16 from '../images/img_16.jpeg';
import img_17 from '../images/img_17.jpeg';
import img_18 from '../images/img_18.jpeg';
import img_19 from '../images/img_19.jpeg';
import img_2 from '../images/img_2.jpeg';
import img_20 from '../images/img_20.jpeg';
import img_3 from '../images/img_3.jpeg';
import img_4 from '../images/img_4.jpeg';
import img_5 from '../images/img_5.jpeg';
import img_6 from '../images/img_6.jpeg';
import img_7 from '../images/img_7.jpeg';
import img_8 from '../images/img_8.jpeg';
import img_9 from '../images/img_9.jpeg';
import './ImageSlider.css';

const ACCENT = '#ef6a1d';
const ACCENT_HOVER = '#d85a10';
const NAVY = '#0b2447';

const images = [
  img_1, img_2, img_3, img_4, img_5, img_6, img_7, img_8, img_9,
  img_11, img_12, img_13, img_14, img_15, img_16, img_17, img_18, img_19, img_20,
];

const N = images.length;
const wrap = (i) => ((i % N) + N) % N;

/* ─────────── Lightbox ─────────── */
const Lightbox = ({ openIndex, onClose, onPrev, onNext }) => {
  useEffect(() => {
    if (openIndex == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openIndex, onClose, onNext, onPrev]);

  return (
    <AnimatePresence>
      {openIndex != null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(6,26,54,0.92)',
            backdropFilter: 'blur(8px)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            sx={{
              position: 'fixed',
              top: 20,
              right: 20,
              color: '#fff',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              width: 48,
              height: 48,
              zIndex: 2,
              '&:hover': { background: ACCENT, borderColor: ACCENT },
            }}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            sx={{
              position: 'fixed',
              top: '50%',
              left: { xs: 10, md: 30 },
              transform: 'translateY(-50%)',
              color: '#fff',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              width: { xs: 44, md: 56 },
              height: { xs: 44, md: 56 },
              zIndex: 2,
              '&:hover': { background: ACCENT, borderColor: ACCENT },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            sx={{
              position: 'fixed',
              top: '50%',
              right: { xs: 10, md: 30 },
              transform: 'translateY(-50%)',
              color: '#fff',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              width: { xs: 44, md: 56 },
              height: { xs: 44, md: 56 },
              zIndex: 2,
              '&:hover': { background: ACCENT, borderColor: ACCENT },
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          <motion.img
            key={openIndex}
            src={images[openIndex]}
            alt={`Gallery ${openIndex + 1}`}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '85vh',
              borderRadius: 12,
              boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
              objectFit: 'contain',
            }}
          />

          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: 600,
              letterSpacing: 0.6,
              px: 2,
              py: 0.8,
              borderRadius: 5,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {String(openIndex + 1).padStart(2, '0')} /{' '}
            {String(N).padStart(2, '0')}
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ─────────── Phone Frame ─────────── */
const PhoneFrame = ({
  src,
  index,
  onOpen,
  size = 'lg',
}) => {
  const isLg = size === 'lg';
  return (
    <Box
      onClick={() => onOpen && onOpen(index)}
      sx={{
        position: 'relative',
        cursor: onOpen ? 'pointer' : 'default',
        width: isLg
          ? { xs: 240, sm: 260, md: 280 }
          : { xs: 180, sm: 200, md: 220 },
        height: isLg
          ? { xs: 500, sm: 540, md: 580 }
          : { xs: 380, sm: 420, md: 460 },
        borderRadius: '40px',
        background:
          'linear-gradient(160deg, #1a2540 0%, #0b1426 50%, #04101f 100%)',
        padding: '10px',
        boxShadow: isLg
          ? `0 30px 60px -10px rgba(6,26,54,0.45),
             0 18px 36px -12px rgba(6,26,54,0.3),
             inset 0 0 0 1.5px rgba(255,255,255,0.08),
             inset 0 1px 0 rgba(255,255,255,0.12)`
          : `0 18px 32px -10px rgba(6,26,54,0.25),
             inset 0 0 0 1px rgba(255,255,255,0.06)`,
        transition: 'box-shadow 0.4s ease',
        '&:hover .phone-screen img': onOpen
          ? { transform: 'scale(1.04)' }
          : {},
        '&:hover .phone-zoom': onOpen
          ? { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }
          : {},
        '&:hover .phone-overlay': onOpen ? { opacity: 1 } : {},
      }}
    >
      {/* Side buttons */}
      <Box
        sx={{
          position: 'absolute',
          right: -2,
          top: '24%',
          width: 3,
          height: 56,
          borderRadius: '0 2px 2px 0',
          background: 'linear-gradient(180deg, #1a2540, #04101f)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: -2,
          top: '20%',
          width: 3,
          height: 36,
          borderRadius: '2px 0 0 2px',
          background: 'linear-gradient(180deg, #1a2540, #04101f)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: -2,
          top: '30%',
          width: 3,
          height: 56,
          borderRadius: '2px 0 0 2px',
          background: 'linear-gradient(180deg, #1a2540, #04101f)',
        }}
      />

      {/* Screen */}
      <Box
        className="phone-screen"
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          borderRadius: '32px',
          overflow: 'hidden',
          background: '#000',
        }}
      >
        <Box
          component="img"
          src={src}
          alt={`Gallery ${index + 1}`}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            transition: 'transform 0.7s ease',
          }}
        />

        {/* Dynamic island */}
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 90,
            height: 24,
            borderRadius: 14,
            background: '#04101f',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            pr: 1,
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#22c55e',
              opacity: 0.7,
            }}
          />
        </Box>

        {/* Glass reflection */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(120deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 35%)',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />

        {onOpen && (
          <>
            <Box
              className="phone-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(6,26,54,0) 40%, rgba(6,26,54,0.7) 100%)',
                opacity: 0,
                transition: 'opacity 0.35s ease',
                zIndex: 2,
              }}
            />
            <Box
              className="phone-zoom"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) scale(0.8)',
                opacity: 0,
                width: 54,
                height: 54,
                borderRadius: '50%',
                background: ACCENT,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 28px rgba(239,106,29,0.55)',
                transition: 'all 0.35s ease',
                zIndex: 3,
                '& svg': { fontSize: '1.5rem' },
              }}
            >
              <ZoomInIcon />
            </Box>
          </>
        )}

        {/* Index pill */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 14,
            left: '50%',
            transform: 'translateX(-50%)',
            px: 1.3,
            py: 0.3,
            borderRadius: 5,
            background: 'rgba(6,26,54,0.75)',
            backdropFilter: 'blur(8px)',
            color: '#fff',
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: 0.8,
            fontVariantNumeric: 'tabular-nums',
            zIndex: 3,
          }}
        >
          {String(index + 1).padStart(2, '0')} / {String(N).padStart(2, '0')}
        </Box>
      </Box>
    </Box>
  );
};

/* ─────────── Arrow ─────────── */
const Arrow = ({ direction, onClick }) => {
  const isPrev = direction === 'prev';
  return (
    <IconButton
      onClick={onClick}
      aria-label={isPrev ? 'Previous' : 'Next'}
      sx={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [isPrev ? 'left' : 'right']: { xs: 4, md: 16 },
        zIndex: 10,
        color: '#fff',
        background: ACCENT,
        width: { xs: 44, md: 52 },
        height: { xs: 44, md: 52 },
        boxShadow: '0 8px 18px rgba(239,106,29,0.35)',
        '&:hover': {
          background: ACCENT_HOVER,
          transform: 'translateY(-50%) scale(1.06)',
        },
        transition: 'all 0.2s ease',
      }}
    >
      {isPrev ? <ChevronLeftIcon /> : <ChevronRightIcon />}
    </IconButton>
  );
};

/* ─────────── Infinite Carousel ─────────── */
const InfiniteCarousel = ({ active, direction, onOpen, isMobile }) => {
  const prevIdx = wrap(active - 1);
  const nextIdx = wrap(active + 1);

  // How far peek phones sit from center
  const peekX = isMobile ? '78%' : '62%';

  const slideVariants = {
    enter: (d) => ({ x: d > 0 ? 240 : -240, opacity: 0, scale: 0.85 }),
    center: { x: 0, opacity: 1, scale: 1 },
    exit: (d) => ({ x: d > 0 ? -240 : 240, opacity: 0, scale: 0.85 }),
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: 540, sm: 580, md: 620 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Left peek */}
      {!isMobile && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: `translate(calc(-50% - ${peekX}), -50%) scale(0.72)`,
            opacity: 0.4,
            filter: 'saturate(0.7)',
            pointerEvents: 'none',
            transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
            zIndex: 1,
          }}
          key={`peek-prev-${prevIdx}`}
        >
          <PhoneFrame src={images[prevIdx]} index={prevIdx} size="sm" />
        </Box>
      )}

      {/* Active center — AnimatePresence handles direction-aware enter/exit */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 3,
        }}
      >
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={active}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
              opacity: { duration: 0.45 },
              scale: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
            }}
          >
            <PhoneFrame src={images[active]} index={active} onOpen={onOpen} />
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Right peek */}
      {!isMobile && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: `translate(calc(-50% + ${peekX}), -50%) scale(0.72)`,
            opacity: 0.4,
            filter: 'saturate(0.7)',
            pointerEvents: 'none',
            transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
            zIndex: 1,
          }}
          key={`peek-next-${nextIdx}`}
        >
          <PhoneFrame src={images[nextIdx]} index={nextIdx} size="sm" />
        </Box>
      )}
    </Box>
  );
};

/* ─────────── Section ─────────── */
const ImageSlider = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = useCallback((i) => setOpenIndex(i), []);
  const handleClose = useCallback(() => setOpenIndex(null), []);

  const goNext = useCallback(() => {
    setDirection(1);
    setActive((a) => wrap(a + 1));
  }, []);
  const goPrev = useCallback(() => {
    setDirection(-1);
    setActive((a) => wrap(a - 1));
  }, []);

  const handleLightboxPrev = useCallback(
    () => setOpenIndex((i) => (i == null ? null : wrap(i - 1))),
    []
  );
  const handleLightboxNext = useCallback(
    () => setOpenIndex((i) => (i == null ? null : wrap(i + 1))),
    []
  );

  // Auto-advance
  useEffect(() => {
    if (paused || openIndex != null) return undefined;
    const id = setTimeout(goNext, 4500);
    return () => clearTimeout(id);
  }, [active, paused, openIndex, goNext]);

  // Keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (openIndex != null) return;
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, openIndex]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 50) goPrev();
    else if (dx < -50) goNext();
    touchStartX.current = null;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 6, md: 9 },
        background:
          'linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 60%, #f8fafc 100%)',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -160,
          right: -120,
          width: 460,
          height: 460,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(239,106,29,0.18) 0%, rgba(239,106,29,0) 70%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -160,
          left: -120,
          width: 460,
          height: 460,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(11,36,71,0.12) 0%, rgba(11,36,71,0) 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.8,
              px: 1.6,
              py: 0.5,
              mb: 2,
              borderRadius: 5,
              background: 'rgba(239,106,29,0.1)',
              border: '1px solid rgba(239,106,29,0.25)',
              color: ACCENT,
              fontSize: '0.78rem',
              fontWeight: 700,
              letterSpacing: 0.6,
            }}
          >
            <PhotoLibraryIcon sx={{ fontSize: '0.95rem' }} />
            OUR GALLERY
          </Box>
          <Box
            component="h2"
            sx={{
              m: 0,
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              fontWeight: 800,
              color: NAVY,
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            Glimpses from{' '}
            <Box component="span" sx={{ color: ACCENT }}>
              our journey
            </Box>
          </Box>
          <Box
            sx={{
              maxWidth: 580,
              mx: 'auto',
              color: '#475569',
              fontSize: { xs: '0.95rem', md: '1.05rem' },
            }}
          >
            A peek into our events, client meets and team moments that shaped
            who we are today.
          </Box>
        </Box>

        {/* Carousel */}
        <Box
          sx={{ position: 'relative' }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <Arrow direction="prev" onClick={goPrev} />
          <Arrow direction="next" onClick={goNext} />

          <InfiniteCarousel
            active={active}
            direction={direction}
            onOpen={handleOpen}
            isMobile={isMobile}
          />

          {/* Counter + progress bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              maxWidth: 360,
              mx: 'auto',
              mt: { xs: 1, md: 2 },
              px: 2,
            }}
          >
            <Box
              sx={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: NAVY,
                fontVariantNumeric: 'tabular-nums',
                minWidth: 26,
                textAlign: 'right',
              }}
            >
              {String(active + 1).padStart(2, '0')}
            </Box>
            <Box
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 4,
                background: 'rgba(11,36,71,0.12)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${((active + 1) / N) * 100}%`,
                  background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
                  borderRadius: 4,
                  transition: 'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
            </Box>
            <Box
              sx={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#64748b',
                fontVariantNumeric: 'tabular-nums',
                minWidth: 26,
              }}
            >
              {String(N).padStart(2, '0')}
            </Box>
          </Box>
        </Box>
      </Container>

      <Lightbox
        openIndex={openIndex}
        onClose={handleClose}
        onPrev={handleLightboxPrev}
        onNext={handleLightboxNext}
      />
    </Box>
  );
};

export default ImageSlider;
